/**
 * Kiosk Settings API Client
 * Handles all HTTP communication with BackOffice API
 * Matches Flutter implementation: kiosk/v1/admin/ endpoints
 */

import { BASE_KIOSK_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { KioskSettingsDto, UpdateKioskSettingsRequest } from '../types/kiosk-settings-types';

/**
 * API client for kiosk settings endpoints
 * Uses centralized ApiErrorMiddleware for error handling
 */
export class KioskSettingsApi {
  constructor(
    private readonly baseUrl: string = BASE_KIOSK_API,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Fetch kiosk settings for an operator
   * Matches Flutter: GET /kiosk/v1/admin/operator?operatorId={id}
   * @param operatorId - The operator ID
   * @returns Promise with KioskSettingsDto
   * @throws Error if API call fails
   */
  async getSettings(operatorId: number): Promise<KioskSettingsDto> {
    const endpoint = `/admin/operator?operatorId=${operatorId}`;

    try {
      const headers = await this.tokenManager.getAuthHeaders();
      const fullUrl = `${this.baseUrl}${endpoint}`;

      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        fullUrl,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...headers
          }
        },
        'Kiosk Settings API',
        endpoint
      );

      if (!response.ok) {
        throw new Error(
          `API Error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      // Flutter pattern: DbActor.fromJson parses settings string with jsonDecode
      // { id, countryCode, partyId, settings: "{...JSON...}" }
      let settingsData: any = { id: data?.id };

      try {
        let nested: any = {};
        if (data?.settings && typeof data.settings === 'string') {
          nested = JSON.parse(data.settings);
        } else if (data?.settings && typeof data.settings === 'object') {
          nested = data.settings;
        }

        settingsData = {
          ...nested,
          id: data.id,
          countryCode: data.countryCode,
          partyId: data.partyId,
        };
      } catch (parseError) {
        throw new Error(`Failed to parse kiosk settings: ${parseError}`);
      }

      return settingsData as KioskSettingsDto;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update kiosk settings
   * Matches Flutter: POST /kiosk/v1/admin/addOperator
   * @param operatorId - The operator ID
   * @param settings - Full settings object (includes countryCode & partyId from GET)
   * @returns Promise<void>
   * @throws Error if API call fails
   */
  async updateSettings(
    operatorId: number,
    settings: KioskSettingsDto
  ): Promise<void> {
    const endpoint = `/admin/addOperator`;

    try {
      // Wrap settings in DbActor format to match Flutter's API
      // Use actual countryCode & partyId from settings (preserved from GET response)
      const dbActor = {
        id: operatorId,
        countryCode: settings.countryCode || 'US',  // Use from settings or fallback
        partyId: settings.partyId || 'NayaxVend',    // Use from settings or fallback
        settings: JSON.stringify(settings) // API expects stringified settings
      };

      const headers = await this.tokenManager.getAuthHeaders();
      const fullUrl = `${this.baseUrl}${endpoint}`;

      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        fullUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers
          },
          body: JSON.stringify(dbActor)
        },
        'Kiosk Settings API',
        endpoint
      );

      if (!response.ok) {
        throw new Error(
          `API Error: ${response.status} ${response.statusText}`
        );
      }

    } catch (error) {
      throw error;
    }
  }

  /**
   * Health check - verify API connectivity
   * @returns Promise<boolean>
   */
  async healthCheck(): Promise<boolean> {
    try {
      const headers = await this.tokenManager.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/admin`, {
        method: 'OPTIONS',
        headers
      });
      return response.ok;
    } catch {
      return false;
    }
  }
}
